import type { Vec2, InputState } from '../types';
import { TiltInput } from './TiltInput';
import { TouchInput } from './TouchInput';
import { KeyboardTilt } from './KeyboardTilt';
import { DragTiltInput } from './DragTiltInput';

/**
 * Centralized input routing.
 * Owns all input sources and provides a single per-frame snapshot.
 * Handles tilt source selection and pinball orientation mapping.
 */
export class InputRouter {
  readonly tiltInput: TiltInput;
  readonly touchInput: TouchInput;
  readonly keyboardTilt: KeyboardTilt;
  readonly dragTiltInput: DragTiltInput;

  /** Cached input state, updated each poll() */
  private cached: InputState = {
    tilt: { x: 0, y: 0 },
    rawTilt: { x: 0, y: 0 },
    leftFlipper: false,
    rightFlipper: false,
    motionPermission: 'unknown',
  };

  /** Whether device has touch capability (cached on construction) */
  readonly isTouchDevice: boolean;

  constructor() {
    this.tiltInput = new TiltInput();
    this.touchInput = new TouchInput();
    this.keyboardTilt = new KeyboardTilt();
    this.dragTiltInput = new DragTiltInput();

    this.isTouchDevice =
      'ontouchstart' in window ||
      ((navigator as { maxTouchPoints?: number }).maxTouchPoints ?? 0) > 0;
  }

  /** Attach pointer-based inputs to canvas */
  attach(canvas: HTMLCanvasElement): void {
    this.touchInput.attach(canvas);
    this.dragTiltInput.attach(canvas);
  }

  /**
   * Poll all inputs and return unified state.
   * Call once per frame before update().
   */
  poll(dt: number): InputState {
    // Update tilt simulations
    this.keyboardTilt.update(dt);
    this.dragTiltInput.update(dt);

    // Select tilt source based on device and permissions
    const tilt = this.selectTiltSource();

    // Flipper state - already in game space from TouchInput
    // (TouchInput handles the pinball orientation swap internally)
    const leftFlipper = this.touchInput.leftFlipper;
    const rightFlipper = this.touchInput.rightFlipper;

    this.cached = {
      tilt,
      rawTilt: this.tiltInput.rawTilt,
      leftFlipper,
      rightFlipper,
      motionPermission: this.tiltInput.permission,
    };

    return this.cached;
  }

  /** Get last polled state without re-polling */
  get state(): InputState {
    return this.cached;
  }

  /**
   * Select the appropriate tilt source based on device type and permissions.
   * Priority:
   * 1. Device motion if touch device + permission granted
   * 2. Drag tilt if touch device without motion
   * 3. Keyboard tilt on desktop
   */
  private selectTiltSource(): Vec2 {
    if (this.isTouchDevice && this.tiltInput.permission === 'granted') {
      return this.tiltInput.tilt;
    }
    if (this.isTouchDevice) {
      return this.dragTiltInput.tilt;
    }
    return this.keyboardTilt.tilt;
  }

  /** Configure tilt input from settings */
  applySettings(settings: {
    invertTiltX?: boolean;
    invertTiltY?: boolean;
    tiltSensitivity?: number;
  }): void {
    if (settings.invertTiltX !== undefined) {
      this.tiltInput.invertX = settings.invertTiltX;
    }
    if (settings.invertTiltY !== undefined) {
      this.tiltInput.invertY = settings.invertTiltY;
    }
    if (settings.tiltSensitivity !== undefined) {
      this.tiltInput.sensitivity = settings.tiltSensitivity;
    }
  }

  /** Set calibration offset */
  setCalibration(offset: Vec2): void {
    this.tiltInput.setCalibration(offset);
  }

  /** Calibrate current orientation as neutral */
  calibrate(): Vec2 {
    this.tiltInput.calibrate();
    return this.tiltInput.calibrationOffset;
  }

  /** Request motion permission (must be called from user gesture) */
  async requestMotionPermission(): Promise<'granted' | 'denied' | 'unavailable' | 'unknown'> {
    return this.tiltInput.requestPermission();
  }

  /** Check if motion needs user gesture for permission (iOS Safari) */
  needsMotionGesture(): boolean {
    if (this.tiltInput.permission === 'granted' || this.tiltInput.permission === 'unavailable') {
      return false;
    }
    if (!this.isTouchDevice) {
      return false;
    }
    const DOE = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    return typeof DOE.requestPermission === 'function';
  }

  dispose(): void {
    this.tiltInput.dispose();
    this.touchInput.dispose();
    this.keyboardTilt.dispose();
    this.dragTiltInput.dispose();
  }
}
